import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { Employee } from '@app/models/employee/employee.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import { TimesheetOverlapErrorHandlingService } from '@app/modules/time-tracking/services';
import { AuthService, NotifyService } from '@app/services';
import { PayrollValidationHttpErrorCodes } from '@time-tracking/meta/time-tracking.meta';

@Component({
    templateUrl: './timesheets-edit.template.html',
})
export class TimesheetsEditView implements OnInit {
    employee: Employee | null = null;
    timesheetEntry: TimesheetEntry;

    isLoading = true;
    hasLoadedData = false;
    projectCanBeChanged = true;
    enteringFor = 'other';
    overlapErrors: string[] = [];

    constructor(
        private authService: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private notifyService: NotifyService,
        private timesheetOverlapErrorHandlingService: TimesheetOverlapErrorHandlingService
    ) {
        this.timesheetEntry = this.route.snapshot.data.models.get('timesheetEntry');
    }

    async ngOnInit(): Promise<void> {
        if (this.timesheetEntry.isApproved && this.authService.cant(Platform.timeTracking.permission.view)) {
            // if the timesheet is approved, and the user isn't an admin, they should not be here
            this.router.navigate(['/dashboard']);
        }

        // this component is used for the general timesheet edit,
        // and my timesheet edit. The only difference is that we tell
        // the children not to show the user name.
        if (this.location.path().includes('my-timesheet')) {
            this.enteringFor = 'self';
        }

        this.loadData();
    }

    async onSave(): Promise<void> {
        try {
            this.isLoading = true;
            await this.timesheetEntry.save();
            this.notifyService.success('time-tracking.enterHoursPage.success');
            this.location.back();
        } catch (e) {
            const { overlapErrors } = this.timesheetOverlapErrorHandlingService.handleHttpError(e as HttpErrorResponse);

            this.overlapErrors = overlapErrors;

            this.isLoading = false;
            if (!PayrollValidationHttpErrorCodes.includes(e.httpStatus ?? e.status)) {
                throw e;
            }
        }
    }

    /**
     * loadData will load the employee for the timesheet entry
     * but with their time tracking project assignments.
     * This is so we can display only their projects in the dropdown.
     */
    private async loadData(): Promise<void> {
        const authEmployee = this.authService.employee as Employee;
        this.employee = await Employee.param('company', authEmployee.companyId)
            .with(['timeTrackingProjectAssignmentsUserCanEnterHoursFor.project', 'activeModuleAssignments.module'])
            .find(this.timesheetEntry.employeeId);

        this.timesheetEntry.employee = this.employee as Employee;

        this.projectCanBeChanged = this.canProjectBeChanged();

        this.hasLoadedData = true;
        this.isLoading = false;
    }

    private canProjectBeChanged(): boolean {
        // if the current user is not an admin, and they are on this view
        // then the project can be changed
        if (this.authService.cant(Platform.timeTracking.permission.view)) {
            return true;
        }

        if (this.timesheetEntry.project?.isArchived) {
            return false;
        }

        // no longer on project
        if (
            this.timesheetEntry.project &&
            !this.employee?.timeTrackingProjectAssignmentsUserCanEnterHoursFor.some(
                (assignment) => assignment.project.id === this.timesheetEntry.project?.id
            )
        ) {
            return false;
        }

        if (!this.employee?.hasModule(Platform.modules.timeTracking)) {
            return false;
        }

        return true;
    }
}
